import React, { useEffect, useState } from 'react';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VideocamIcon from '@mui/icons-material/Videocam';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Avatar, Button, ButtonGroup, Divider, Stack, Typography } from '@mui/material';
import { darken, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { PrivateCallStatusEnum } from '@juno/client-api/model';
import { usePrivateCall, useSessionContext, useSettings } from '@juno/utils';
import { RingtoneURL } from '../SessionPage/constants';
import { useSound } from '../SessionPage/hooks/useSound';

const IncomingCallInner: React.FC = () => {
  const { callState } = useSessionContext();
  const { user, isSmallScreen } = useSettings();
  const { handleAccept, handleDecline, handleJoinInNewTab, handleTimeout } = usePrivateCall();
  const sound = useSound(RingtoneURL);
  const theme = useTheme();

  const [timeLeft, setTimeLeft] = useState(15);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleTimeout();
    }
  }, [timeLeft]);

  useEffect(() => {
    sound.play();
    return () => {
      sound.stop();
    };
  }, []);

  const decline = async () => {
    handleDecline();
  };

  return (
    <motion.div
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
      }}
      initial={{ opacity: 0, bottom: -300 }}
      exit={{ opacity: 0, bottom: -300 }}
      animate={{ opacity: 1, bottom: 80 }}
      transition={{ duration: 0.2 }}
    >
      <Stack
        sx={{
          py: 2,
          px: 2.5,
          background: '#1f1f1f',
          width: 600,
          maxWidth: '95vw',
          height: 290,
          borderRadius: 2,
        }}
        alignItems='center'
      >
        <Typography variant='subtitle2' sx={{ color: '#f4f4f4', pb: 2 }}>
          Join Incoming Private Call Request
        </Typography>
        <Divider sx={{ borderColor: '#cac4d0', width: '100%' }} />
        <Stack direction='row' alignItems={'center'} sx={{ py: 3 }}>
          <Stack alignItems='center'>
            <Avatar src={callState.otherUser?.avatar} sx={{ height: 100, width: 100, mb: 1.25 }} />
            <Typography variant='subtitle2' sx={{ color: '#f4f4f4' }}>
              {callState.otherUser?.first_name} {callState.otherUser?.last_name}
            </Typography>
          </Stack>
          <ConnectWithoutContactIcon
            sx={{ color: '#f4f4f4', fontSize: 40, mx: isSmallScreen ? 5 : 8 }}
          />
          <Stack alignItems='center'>
            <Avatar src={user?.avatar || ''} sx={{ height: 100, width: 100, mb: 1.25 }} />
            <Typography variant='subtitle2' sx={{ color: '#f4f4f4' }}>
              You
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' gap={2} sx={{ width: '100%' }}>
          <ButtonGroup
            variant='contained'
            aria-label='Join with Audio'
            sx={{
              '& .MuiButtonGroup-grouped': {
                borderColor: darken(theme.palette.success.main, 0.15),
              },
              width: '100%',
            }}
          >
            <Button
              variant='contained'
              color='success'
              fullWidth
              onClick={() => handleAccept(false)}
              aria-label='Accept audio call'
              startIcon={<VolumeUpIcon />}
            >
              Audio
            </Button>
            <Button
              aria-label='Accept audio call in new window'
              variant='contained'
              color='success'
              size='small'
              endIcon={<OpenInNewIcon />}
              sx={{
                '& .MuiButton-endIcon': {
                  marginLeft: 0,
                },
              }}
              onClick={() => handleJoinInNewTab(false)}
            ></Button>
          </ButtonGroup>
          <ButtonGroup
            variant='contained'
            aria-label='Join with Video'
            sx={{
              '& .MuiButtonGroup-grouped': {
                borderColor: darken(theme.palette.success.main, 0.15),
              },
              width: '100%',
            }}
          >
            <Button
              variant='contained'
              color='success'
              fullWidth
              onClick={() => handleAccept(true)}
              aria-label='Accept video call'
              startIcon={<VideocamIcon />}
            >
              Video
            </Button>
            <Button
              aria-label='Accept video call in new window'
              variant='contained'
              color='success'
              size='small'
              endIcon={<OpenInNewIcon />}
              sx={{
                '& .MuiButton-endIcon': {
                  marginLeft: 0,
                },
              }}
              onClick={() => handleJoinInNewTab(true)}
            ></Button>
          </ButtonGroup>
          <Button
            variant='contained'
            color='error'
            fullWidth
            onClick={decline}
            aria-label='Decline call'
            startIcon={<CallEndIcon />}
          >
            Decline
          </Button>
        </Stack>
      </Stack>
    </motion.div>
  );
};

const IncomingCall: React.FC = () => {
  const { callState } = useSessionContext();
  return (
    /* 
      Using a key in AnimatePresence allows us to destroy the component
      once the call's timer ends (the call is not answered)
    */
    <AnimatePresence key={callState.privateCallId}>
      {callState.status === PrivateCallStatusEnum.pending &&
        !callState.dialogOpen &&
        !callState.isCaller && <IncomingCallInner />}
    </AnimatePresence>
  );
};

export default IncomingCall;
